/**
 * React Query 클라이언트 설정
 *
 * 캐싱 전략:
 * - 기본 staleTime: 5분 (불필요한 재요청 방지)
 * - gcTime: 10분 (메모리에서 제거되기 전 유지 시간)
 * - refetchOnWindowFocus: false (탭 전환 시 자동 요청 방지 → ~30% 요청 감소)
 * - refetchOnReconnect: false (네트워크 재연결 시 자동 요청 방지)
 *
 * 개별 쿼리별 설정 (usePlaylistQueries.ts 등):
 * - 목록 조회: staleTime 30초 (빠른 업데이트 필요)
 * - 상세 조회: staleTime 1분
 * - 상태 조회: staleTime 10초 (생성 중 실시간 모니터링)
 *
 * Phase 17 성능 최적화 적용 (2025-12-29)
 */
import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,        // 5분 - 대부분의 데이터는 자주 변경되지 않음
      gcTime: 10 * 60 * 1000,          // 10분 - 메모리에서 캐시 유지 시간
      retry: 1,                         // 1회 재시도 (네트워크 일시적 장애 대응)
      refetchOnWindowFocus: false,      // 탭 전환 시 자동 재요청 비활성화 (성능 최적화)
      refetchOnReconnect: false,        // 네트워크 재연결 시 자동 재요청 비활성화
      refetchOnMount: true,               // 컴포넌트 마운트 시 stale이면 재요청 (기본 동작)
      throwOnError: false,              // 에러를 throw하지 않고 error 상태로 관리
    },
    mutations: {
      retry: 0,                         // mutation은 재시도하지 않음 (중복 작업 방지)
      throwOnError: false,
    },
  },
});

/**
 * 캐시 무효화 유틸리티 함수
 * 사용자 액션 시 명시적으로 캐시를 무효화할 때 사용
 */
export function invalidateAllQueries() {
  queryClient.invalidateQueries();
}

/**
 * 특정 쿼리 키의 캐시 무효화
 * @param queryKey - 무효화할 쿼리 키
 */
export function invalidateQuery(queryKey: readonly unknown[]) {
  queryClient.invalidateQueries({ queryKey });
}
